import { Volume2 } from "lucide-react";
import { speak, cancelSpeech } from "@/lib/speech";

// Pairs adjacent missed/extra words from the diff into "you said X instead of Y"
const analyse = (ops) => {
  const words = [], subs = [];
  for (let i = 0; i < ops.length; i++) {
    const o = ops[i];
    if (o.t === "eq") words.push({ w: o.w, ok: true });
    else if (o.t === "miss") {
      const next = ops[i + 1];
      const heard = next?.t === "extra" ? next.w : null;
      if (heard) { subs.push({ target: o.w, heard }); i++; }
      words.push({ w: o.w, ok: false, heard });
    }
  }
  return { words, subs, flagged: words.filter((x) => !x.ok) };
};

const say = (w) => { cancelSpeech(); speak(w, { rate: 0.7 }); };

export const PronunciationPanel = ({ diff, response }) => {
  const { words, subs, flagged } = analyse(diff.ops);
  const clarity = words.length ? Math.round(((words.length - flagged.length) / words.length) * 100) : 0;
  return (
    <div data-testid="pronunciation-panel" className="wrap-anywhere box-border w-full min-w-0 max-w-full max-h-80 space-y-3 overflow-y-auto overflow-x-hidden rounded-lg border border-slate-200 px-3 py-2 sm:px-4 sm:py-3">
      <div className="flex flex-wrap items-center justify-between gap-x-3 gap-y-1">
        <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-slate-500">Pronunciation check</span>
        <span data-testid="pronunciation-clarity" className="font-mono text-xs font-bold text-slate-700">{clarity}% {response.spoken ? "of words recognised" : "of words matched (typed)"}</span>
      </div>
      {!response.spoken && <p data-testid="pronunciation-typed-note" className="text-xs text-amber-600">This answer was typed, so it can't show pronunciation. Allow the microphone and speak to get real pronunciation feedback.</p>}
      <p className="text-sm leading-8 sm:text-base" data-testid="pronunciation-words">
        {words.map((x, i) => x.ok
          ? <span key={i} className="mr-1 text-slate-700">{x.w}</span>
          : <button key={i} data-testid={`mispronounced-word-${i}`} onClick={() => say(x.w)} title={x.heard ? `Heard "${x.heard}" — click to hear it` : "Not recognised — click to hear it"} className="mr-1 rounded bg-rose-100 px-1 font-semibold text-rose-700 underline decoration-dotted underline-offset-4 transition-colors hover:bg-rose-200">{x.w}</button>)}
      </p>
      {flagged.length > 0 && (
        <div className="space-y-2">
          <div className="text-xs font-semibold text-slate-600">Practise these words ({flagged.length})</div>
          <div className="flex flex-wrap gap-2">
            {[...new Map(flagged.map((f) => [f.w, f])).values()].map((f) => (
              <button key={f.w} data-testid={`practice-word-${f.w}`} onClick={() => say(f.w)} className="inline-flex max-w-full items-center gap-1 whitespace-normal rounded-full border border-rose-200 bg-white px-3 py-1 text-left text-xs text-rose-700 transition-colors hover:bg-rose-50"><Volume2 className="h-3 w-3 shrink-0" /><span className="min-w-0">
                {f.w}{f.heard && <span className="text-slate-400"> · heard "{f.heard}"</span>}</span>
              </button>
            ))}
          </div>
        </div>
      )}
      {flagged.length === 0 && words.length > 0 && <p className="text-xs text-emerald-600">Every word was recognised clearly. Great articulation.</p>}
      {subs.length > 0 && <p className="text-xs text-slate-400">Tip: replaced words often mean a vowel or ending sound was unclear. Listen at slow speed, then repeat.</p>}
    </div>
  );
};
